import { Button, ScrollArea, Search, Tooltip } from '@ibsys/kit';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon, ChevronsDownUpIcon, ChevronsUpDownIcon, CopyIcon } from 'lucide-react';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { scrollIntoViewWithinContainer } from '@/shared/lib';
import { JsonNodeRenderer } from './JsonNodeRenderer';
import { buildTree, collectAllPaths, INDENT_SIZE } from './lib';
import { JsonValue } from './types';

/**
 * Пропсы таба «Сырые данные».
 */
type Props = {
  /** Сырые данные диалога — содержимое поля externalRawData из ответа GET /conversation/{id} */
  data?: Record<string, unknown> | null;
};

const COPY_FEEDBACK_TIMEOUT = 2000;

/**
 * Таб «Сырые данные» в карточке диалога.
 * Отображает поле externalRawData как форматированный JSON со сворачиваемым деревом,
 * подсветкой синтаксиса, копированием в буфер и навигацией по совпадениям поиска.
 */
export const RawDataTab: FC<Props> = React.memo(({ data }) => {
  const { t } = useTranslation(['dialogs', 'common']);
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedPaths, setCollapsedPaths] = useState<Set<string>>(new Set());
  const [isCopied, setIsCopied] = useState(false);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [totalMatches, setTotalMatches] = useState(0);
  const preRef = useRef<HTMLPreElement>(null);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const tree = useMemo(() => {
    if (data === undefined || data === null) return null;
    const result = buildTree(data as JsonValue, 0, '$');
    if ('kind' in result) return null;
    return result;
  }, [data]);

  const allPaths = useMemo(() => {
    if (!tree) return new Set<string>();
    return collectAllPaths(tree);
  }, [tree]);

  const isAllCollapsed = useMemo(() => {
    if (allPaths.size === 0) return false;
    return collapsedPaths.size >= allPaths.size;
  }, [allPaths, collapsedPaths]);

  const handleCopy = useCallback(() => {
    if (!data) return;
    const text = JSON.stringify(data, null, INDENT_SIZE);
    navigator.clipboard.writeText(text);
    setIsCopied(true);
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = setTimeout(() => setIsCopied(false), COPY_FEEDBACK_TIMEOUT);
  }, [data]);

  // Очищаем таймер копирования при размонтировании
  useEffect(
    () => () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    },
    [],
  );

  const handleToggle = useCallback((path: string) => {
    setCollapsedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  const handleToggleAll = useCallback(() => {
    if (isAllCollapsed) {
      setCollapsedPaths(new Set());
    } else {
      setCollapsedPaths(new Set(allPaths));
    }
  }, [isAllCollapsed, allPaths]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentMatchIndex(0);
  }, []);

  const handlePrevMatch = useCallback(() => {
    setCurrentMatchIndex((prev) => (prev > 0 ? prev - 1 : Math.max(totalMatches - 1, 0)));
  }, [totalMatches]);

  const handleNextMatch = useCallback(() => {
    setCurrentMatchIndex((prev) => (prev < totalMatches - 1 ? prev + 1 : 0));
  }, [totalMatches]);

  // Подсчёт совпадений и подсветка/скролл к текущему — после рендера mark-элементов
  // biome-ignore lint/correctness/useExhaustiveDependencies: searchQuery и collapsedPaths меняют DOM (mark-элементы), нужен пересчёт
  useEffect(() => {
    if (!preRef.current) {
      setTotalMatches(0);
      return;
    }

    const marks = Array.from(preRef.current.getElementsByTagName('mark'));
    setTotalMatches(marks.length);

    for (const mark of marks) {
      mark.classList.remove('ring-2', 'ring-orange-400');
    }

    if (marks.length > 0) {
      const idx = Math.min(currentMatchIndex, marks.length - 1);
      marks[idx].classList.add('ring-2', 'ring-orange-400');
      scrollIntoViewWithinContainer(marks[idx], { align: 'center', behavior: 'smooth' });
    }
  }, [searchQuery, collapsedPaths, currentMatchIndex]);

  if (!tree) {
    return (
      <div className="flex h-full items-center justify-center p-4">
        <p className="text-sm text-muted-foreground">{t('dialogs:noData')}</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-2">
      {/* Панель инструментов */}
      <div className="flex items-center gap-2 px-4 pt-2">
        <Search placeholder={t('common:search')} value={searchQuery} onChange={handleSearchChange} className="flex-1" />

        {searchQuery && (
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {totalMatches > 0 ? `${currentMatchIndex + 1}/${totalMatches}` : '0/0'}
            </span>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('dialogs:rawDataPrevMatch')}
              onClick={handlePrevMatch}
              disabled={totalMatches === 0}
              icon={<ChevronUpIcon />}
            />
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('dialogs:rawDataNextMatch')}
              onClick={handleNextMatch}
              disabled={totalMatches === 0}
              icon={<ChevronDownIcon />}
            />
          </div>
        )}

        <Tooltip tooltip={isAllCollapsed ? t('dialogs:rawDataExpandAll') : t('dialogs:rawDataCollapseAll')}>
          <Button
            variant="ghost"
            size="icon"
            aria-label={isAllCollapsed ? t('dialogs:rawDataExpandAll') : t('dialogs:rawDataCollapseAll')}
            onClick={handleToggleAll}
            icon={isAllCollapsed ? <ChevronsUpDownIcon /> : <ChevronsDownUpIcon />}
          />
        </Tooltip>

        <Tooltip tooltip={isCopied ? t('dialogs:rawDataCopied') : t('dialogs:rawDataCopy')}>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('dialogs:rawDataCopy')}
            onClick={handleCopy}
            icon={isCopied ? <CheckIcon /> : <CopyIcon />}
          />
        </Tooltip>
      </div>

      {/* JSON-дерево */}
      <ScrollArea className="flex-1 w-full">
        <pre ref={preRef} className="font-mono text-sm leading-relaxed p-4 whitespace-pre">
          <code>
            <JsonNodeRenderer
              node={tree}
              collapsedPaths={collapsedPaths}
              onToggle={handleToggle}
              searchQuery={searchQuery}
              isLast
            />
          </code>
        </pre>
      </ScrollArea>
    </div>
  );
});
